import React, { useEffect, useState } from 'react';
import { getGoogleClassroomCourses, GoogleClassroomCourse } from '../services/GoogleClassroomStudentImport.service';
import BackButton from '../../../components/Common/BackButton';

interface GoogleClassroomCourseSelectorProps {
  onCourseSelect: (course: GoogleClassroomCourse) => void;
  onBack: () => void;
  onClose: () => void;
}

const GoogleClassroomCourseSelector: React.FC<GoogleClassroomCourseSelectorProps> = ({
  onCourseSelect,
  onBack,
  onClose,
}) => {
  const [courses, setCourses] = useState<GoogleClassroomCourse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getGoogleClassroomCourses();

        if (data) {
          const list = data.filter(
            (course) =>
              course.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
              (course.section && course.section.toLowerCase().includes(searchTerm.toLowerCase())),
          );
          setCourses(list);
        } else {
          setError('Failed to fetch Google Classroom courses');
        }
      } catch (error) {
        setError('Failed to fetch Google Classroom courses');
        console.error('Error fetching courses:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, []);

  const handleCourseSelect = (course: GoogleClassroomCourse) => {
    onCourseSelect(course);
  };

  if (loading) {
    return (
      <div className="relative sm:w-[360px] w-full">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-storyblue"></div>
          <span className="ml-3 text-lg">Loading courses...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="relative sm:w-[360px] w-full">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
            <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mt-4">Error</h3>
          <p className="text-sm text-gray-500 mt-2">{error}</p>
          <div className="mt-4 flex space-x-3 justify-between">
            <div>
              <BackButton onClick={onBack}></BackButton>
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative sm:w-[360px] w-full">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-storyblue">Select Google Classroom Course</h3>
      </div>

      {/* Search Input */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search courses..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-3 py-2 border-b border-secondaryBorderBackgroundLightGreyColor rounded-md"
        />
      </div>

      {/* Course List */}
      <div className="max-h-64 overflow-y-auto">
        {courses.length === 0 ? (
          <div className="text-center py-8">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No courses found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm ? 'Try adjusting your search terms.' : 'No courses available in Google Classroom.'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {courses.map((course) => (
              <button
                key={course.id}
                onClick={() => handleCourseSelect(course)}
                className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-gray-900 truncate">{course.name}</h4>
                    {course.section && <p className="text-sm text-gray-500 truncate">Section: {course.section}</p>}
                    {course.description && <p className="text-xs text-gray-400 truncate mt-1">{course.description}</p>}
                  </div>
                  <div className="ml-3 flex-shrink-0">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        course.state === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {course.state}
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="mt-6 flex justify-start">
        <div>
          <BackButton onClick={onBack}></BackButton>
        </div>
      </div>
    </div>
  );
};

export default GoogleClassroomCourseSelector;
